/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import * as CampaignApi from "../../../../../api/Campaign";
import { IReadCampaign } from "../../../../../api/Campaign";
import { updateUser } from "../../../../../api/User";
import FooterIcon from "../../../../../assets/icon/footer";
import { RecipientAtom } from "../../../../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../../../../atoms/SavedSearches";
import { UserAtom } from "../../../../../atoms/UserAtom";
import { IUserEmailOptions, IUserIRoute } from "../../../../../generated/Api";
import { SpaceBetween } from "../../../../../styles/general";
import {
  ICampaignStatus,
  INodeTypes,
} from "../../../../../types/CampaignTypes";
import { IOption } from "../../../../../types/CommonTypes";
import { FormatEmail } from "../../../../../utils/FormatEmail";
import StyledAutocompleteSelect from "../../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../../StyledButton";
import { useModal } from "../../../Modal";
import CreateOrEditFooter from "../../CreateOrEditFooter";
import Warning from "../../Warning";

const IconButton = styled.div`
  height: 32px;
  width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;

  border: 1px solid #dddddd;

  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 25px 0px;
  padding: 0px 22px;
  width: 600px;
  height: fit-content;

  @media screen and (max-width: 600px) {
    width: 100%;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 13px;
`;

const Edit = styled.div`
  color: #fb972e;

  display: flex;
  align-items: center;

  cursor: pointer;
`;

const LabelEdit = styled.div`
  font-size: 16px;
  font-weight: 600;

  margin-left: 8px;
`;

const ButtonCss = css`
  min-width: 110px;
  padding: 0px;
  height: 42px;

  margin-left: 13px;
`;

const ItemToolbarContainer = styled.div`
  display: flex;
  grid-gap: 8px;

  position: absolute;
  right: 0px;

  align-items: center;
  margin-top: -6px;
`;

const FooterBackground = styled.div`
  white-space: pre-wrap;
  background-color: #00000008;
  border-radius: 5px;
  padding: 14px;
`;

const IconButtonWithHover = styled(IconButton)`
  color: #aaaaaa !important;
  border: none;

  &:hover {
    color: #fb972e !important;
  }
`;

interface Props {
  user: IUserIRoute;
  setUser: (u: IUserIRoute) => void;
}

type UserFooter = Required<IUserEmailOptions>["footers"][0];

export const LINE_SEPARATOR_OPTIONS: {
  name: string;
  value: UserFooter["hasSeparatorLine"] | null;
}[] = [
  {
    name: "No line separator",
    value: null,
  },
  {
    name: "Before additional footer text",
    value: "BEFORE_FOOTER",
  },
  {
    name: "Before CAN-SPAM",
    value: "BEFORE_CAN",
  },
];

const IsLoadingDeleteFooterAtom = atom<boolean>({
  key: "IsLoadingDeleteFooterAtom",
  default: false,
});

export default function FooterSettings(props: Props): JSX.Element {
  const { user: userFromProps, setUser: setUserFromProps } = props;
  const modal = useModal();
  const [isLoading, setIsLoading] = useRecoilState(IsLoadingDeleteFooterAtom);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const [recipient, setRecipient] = useRecoilState(RecipientAtom);
  const [campaigns, setCampaigns] = useState<IReadCampaign[]>();
  const setUser = useSetRecoilState(UserAtom);

  const [options, setOptions] = useState(
    userFromProps.emailOptions.footers.map((f) => ({
      value: f.id as string,
      name: f.name as string,
    }))
  );

  const [footerInfo, setFooterInfo] = useState(() => {
    if (!recipient.footerId || recipient.setted === "savedSearch")
      return userFromProps.emailOptions!.footers[0];
    const footerFromRecipent = userFromProps.emailOptions!.footers!.find(
      (f) => f.id === recipient.footerId
    );
    return footerFromRecipent;
  });

  const [selectedFooter, setSelectedFooter] = useState<IOption>(() => {
    if (!recipient.footerId || recipient.setted === "savedSearch")
      return { value: "", name: "" };
    const footerFromRecipent = userFromProps.emailOptions!.footers!.find(
      (f) => f.id === recipient.footerId
    );
    return { value: footerFromRecipent.id, name: footerFromRecipent.name };
  });

  const { footer } = FormatEmail.format("", userFromProps as IUserIRoute, {
    footer: footerInfo,
  });

  async function getCampaigns() {
    if (campaigns) return;
    const campaignsResponse = await CampaignApi.readAllCampaigns();
    setCampaigns(campaignsResponse);
  }

  useEffect(() => {
    getCampaigns();
  }, []);

  useEffect(() => {
    if (!selectedFooter.value) {
      recipient.setted !== "savedSearch" &&
        setRecipient((r) => ({ ...recipient, footerId: null }));
      return;
    }

    const footerFromUser = userFromProps.emailOptions.footers.find(
      (f) => f.id === selectedFooter.value
    );

    setFooterInfo(footerFromUser);

    recipient.setted !== "savedSearch" &&
      setRecipient((r) => ({ ...recipient, footerId: footerFromUser.id }));
  }, [selectedFooter, userFromProps]);

  useEffect(() => {
    setOptions(
      userFromProps!.emailOptions!.footers!.map((f) => ({
        value: f.id as string,
        name: f.name as string,
      }))
    );
  }, [userFromProps.emailOptions.footers]);

  async function deleteFooter(option: IOption) {
    if (isLoading) return;

    setIsLoading(true);

    if (userFromProps.emailOptions.footers.length === 1) {
      toast("Footer selected is disabled to remove", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    if (countedSavedSearches.find((s) => s.footerId === option.value)) {
      toast("There is a saved search using this footer", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    const campaignNodeWithFooter = campaigns.find(
      (campaign) =>
        (campaign.status === ICampaignStatus.Active ||
          campaign.status === ICampaignStatus.Inactive ||
          campaign.status === ICampaignStatus.Draft) &&
        !!campaign.nodes.find(
          (node) =>
            node.type === INodeTypes.Email &&
            node.email.footerId === option.value
        )
    );

    if (campaignNodeWithFooter) {
      toast("There is a campaign using this footer", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    const newFooterArray = userFromProps.emailOptions.footers.filter(
      (footer) => footer.id !== option.value
    );

    const isSuccessUpdateUser = await updateUser({
      emailOptions: {
        ...userFromProps.emailOptions,
        footers: newFooterArray,
      },
    });
    if (isSuccessUpdateUser) {
      if (selectedFooter.value === option.value)
        setSelectedFooter({ value: "", name: "" });

      setUser((user) => ({
        ...user,
        emailOptions: {
          ...user.emailOptions,
          footers: newFooterArray,
        },
      }));

      setUserFromProps({
        ...userFromProps,
        emailOptions: {
          ...userFromProps.emailOptions,
          footers: newFooterArray,
        },
      });

      setIsLoading(false);
      toast("Footer deleted", { type: "success" });
      modal.popModal();
    }
  }

  function handleCreateClick() {
    modal.pushModal(
      <CreateOrEditFooter
        setAsDefault={(footer) => {
          let footers = [...userFromProps.emailOptions.footers];
          footers[0] = footer;
          setUserFromProps({
            ...userFromProps,
            emailOptions: {
              ...userFromProps.emailOptions,
              footers: footers,
            },
          });
        }}
        onSave={(footer, footerArray) => {
          setUserFromProps({
            ...userFromProps,
            emailOptions: {
              ...userFromProps.emailOptions,
              footers: footerArray,
            },
          });
        }}
      />,
      "New Footer"
    );
  }

  function handleEditClick() {
    modal.pushModal(
      <CreateOrEditFooter
        footer={footerInfo}
        setAsDefault={(footer) => {
          let footers = [...userFromProps.emailOptions.footers];
          footers[0] = footer;
          setUserFromProps({
            ...userFromProps,
            emailOptions: {
              ...userFromProps.emailOptions,
              footers: footers,
            },
          });
        }}
        onSave={(footer, footerArray) => {
          setSelectedFooter({
            value: footer.id as string,
            name: footer.name as string,
          });

          setUserFromProps({
            ...userFromProps,
            emailOptions: {
              ...userFromProps.emailOptions,
              footers: footerArray,
            },
          });
        }}
      />,
      "Edit Footer"
    );
  }

  return (
    <>
      <FlexColumn>
        <Title>Footer</Title>

        <div
          css={css`
            display: flex;
            flex-direction: row;
            width: 100%;
          `}
        >
          <StyledAutocompleteSelect
            selectedOption={selectedFooter}
            onOptionSelect={(option) => setSelectedFooter(option)}
            placeholder="Select a footer"
            options={options}
            removeCheckIcon
            startIcon={<FooterIcon width={16} height={16} />}
            renderCustomItemToolbar={(option) =>
              options[0].value !== option.value && (
                <ItemToolbarContainer>
                  <IconButtonWithHover
                    onClick={(e) => {
                      e.stopPropagation();

                      modal.pushModal(
                        <Warning
                          IsLoadingAtom={IsLoadingDeleteFooterAtom}
                          icon={"./icons/delete.svg"}
                          title="Delete Footer"
                          description={
                            "Are you sure you want to delete this footer?"
                          }
                          buttonsName={["No", "Yes"]}
                          onOkClicked={() => {
                            deleteFooter(option);
                          }}
                          onCancelClicked={() => modal.popModal()}
                        />
                      );
                    }}
                  >
                    <FaRegTrashAlt />
                  </IconButtonWithHover>
                </ItemToolbarContainer>
              )
            }
          />

          <StyledButton inverted onClick={handleCreateClick} css={ButtonCss}>
            Create New
          </StyledButton>
        </div>
      </FlexColumn>

      <FlexColumn>
        <SpaceBetween
          css={css`
            align-items: flex-start;
          `}
        >
          <Title>Preview</Title>
          <Edit onClick={handleEditClick}>
            {selectedFooter.value && (
              <>
                <FaPencilAlt />
                <LabelEdit>Edit</LabelEdit>
              </>
            )}
          </Edit>
        </SpaceBetween>
        {selectedFooter.value ? (
          <FooterBackground dangerouslySetInnerHTML={{ __html: footer }} />
        ) : (
          <FooterBackground
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              flex-direction: column;
              height: 250px;
            `}
          >
            <img src="/new-email/newemail-list.svg" alt="Preview" />
            <text
              css={css`
                margin-top: 14px;
                font-weight: 400;
                color: #707070;
              `}
            >
              Select a footer to preview
            </text>
          </FooterBackground>
        )}
      </FlexColumn>
    </>
  );
}
